import { useTranslation } from 'react-i18next';
import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('company_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9nikjsit35ii3jdlrpg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormError('系统检测到异常提交，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查您的网络连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 pb-14">
          <Breadcrumb items={[{ label: t('about.title') }]} />

          <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-8">
            {t('about.title')}
          </h1>

          {/* Brand Introduction */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
              <i className="ri-building-2-line text-primary-500 mr-2"></i>
              {t('about.brand')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <p className="text-foreground-500 leading-relaxed">
                  <strong className="text-foreground-800">91高清视频</strong>成立于2015年，是国内领先的在线高清视频平台。我们致力于为观众提供最优质的观影体验，汇聚了海量高清影片资源，涵盖动作、喜剧、科幻、剧情、悬疑等多种类型。
                </p>
                <p className="text-foreground-500 leading-relaxed">
                  平台以"高清画质、流畅观影"为核心理念，采用先进的视频编码技术和CDN加速网络，确保每一位用户都能享受到流畅不缓冲的极致观影体验。无论是经典老片还是最新热门，我们都以最高画质呈现。
                </p>
                <p className="text-foreground-500 leading-relaxed">
                  经过多年的发展，91高清视频已拥有超过5000万注册用户，日均活跃用户突破300万。平台内容库涵盖超过10万部影片，并且每天都在持续更新中。
                </p>
              </div>
              <div className="bg-background-200/60 rounded-xl p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-500">
                    <i className="ri-movie-line text-xl"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground-950">10万+</div>
                    <div className="text-foreground-400 text-sm">海量影片库</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-500/10 text-accent-500">
                    <i className="ri-user-star-line text-xl"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground-950">5000万+</div>
                    <div className="text-foreground-400 text-sm">注册用户</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-500/10 text-secondary-500">
                    <i className="ri-thumb-up-line text-xl"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground-950">98.5%</div>
                    <div className="text-foreground-400 text-sm">用户满意度</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
              <i className="ri-medal-line text-accent-500 mr-2"></i>
              {t('about.reputation')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                {
                  name: '张先生',
                  role: '资深影迷',
                  comment: '用91高清视频三年了，画质真的没话说，蓝光资源特别丰富，播放也很流畅，强烈推荐给喜欢看电影的朋友！',
                  rating: 5,
                },
                {
                  name: '李女士',
                  role: '影视从业者',
                  comment: '作为业内人士，我对画质要求很高。91高清视频的超清画质让我非常满意，分类也很清晰，找片特别方便。',
                  rating: 5,
                },
                {
                  name: '王同学',
                  role: '大学生',
                  comment: '宿舍网速一般，但91高清视频加载速度很快，基本不用缓冲。影片种类也很多，周末和室友一起看片的首选平台。',
                  rating: 4,
                },
                {
                  name: '赵先生',
                  role: 'IT工程师',
                  comment: '技术层面做得很好，视频编码效率高，在有限的带宽下也能保证不错的画质。播放器体验也很流畅。',
                  rating: 5,
                },
                {
                  name: '陈女士',
                  role: '全职妈妈',
                  comment: '闲暇时喜欢看电影放松，91高清视频操作简单，片源更新快，画质清晰，已经成为我日常娱乐的一部分。',
                  rating: 5,
                },
                {
                  name: '刘先生',
                  role: '退休教师',
                  comment: '很多经典老片在这里都能找到高清版本，让我重温了很多年轻时看过的电影，画质修复得非常好。',
                  rating: 4,
                },
              ].map((review, index) => (
                <div key={index} className="bg-background-200/60 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i
                          className={`text-sm ${
                            i < review.rating
                              ? 'ri-star-fill text-accent-500'
                              : 'ri-star-line text-foreground-500'
                          }`}
                        ></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-3">{review.comment}</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-500 text-xs font-bold">
                      {review.name[0]}
                    </div>
                    <div>
                      <div className="text-foreground-800 text-sm font-medium">{review.name}</div>
                      <div className="text-foreground-400 text-xs">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6">
              <i className="ri-mail-line text-secondary-500 mr-2"></i>
              {t('about.contact')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-background-200/60 rounded-xl p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <div className="text-foreground-400 text-xs">电子邮箱</div>
                    <div className="text-foreground-800 text-sm">contact@91hdvideo.com</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/10 text-accent-500">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div>
                    <div className="text-foreground-400 text-xs">客服热线</div>
                    <div className="text-foreground-800 text-sm">400-888-9191</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-500/10 text-secondary-500">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <div className="text-foreground-400 text-xs">公司地址</div>
                    <div className="text-foreground-800 text-sm">北京市朝阳区建国路88号SOHO现代城A座</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <div className="text-foreground-400 text-xs">工作时间</div>
                    <div className="text-foreground-800 text-sm">周一至周日 9:00 - 21:00</div>
                  </div>
                </div>
              </div>

              <div className="bg-background-200/60 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-foreground-900 mb-4 font-heading">发送消息</h3>
                <form
                  data-readdy-form
                  className="space-y-4"
                  onSubmit={handleSubmit}
                >
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-300/60 text-foreground-700 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/50 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-300/60 text-foreground-700 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/50 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="请输入您的消息（最多500字）"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-300/60 text-foreground-700 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                      required
                    ></textarea>
                    <p className="text-foreground-400 text-xs mt-1 text-right">最多500字</p>
                  </div>
                  <div className="form-company-alt-wrap">
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-foreground-950 font-semibold px-6 py-3 rounded-lg text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i> 发送中...
                      </span>
                    ) : (
                      '发送消息'
                    )}
                  </button>
                  {formStatus === 'success' && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-accent-500/10 text-accent-500 text-sm">
                      <i className="ri-checkbox-circle-line"></i>
                      消息发送成功！我们会尽快回复您。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-primary-500/10 text-primary-500 text-sm">
                      <i className="ri-error-warning-line"></i>
                      {formError}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}